import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Loader2, Star, XCircle, FileText, RotateCcw, Truck } from 'lucide-react';
import { toast } from 'sonner';
import OrderTrackingTimeline from '../components/OrderTrackingTimeline';
import { MpOrdersList } from '../components/orders/MpOrdersList';
import { CustomerShippingQuotes } from '../components/orders/ShippingQuotes';
import AccountLayout from '../components/AccountLayout';
import BillingAddress from '../components/BillingAddress';
import AppointmentManager from '../components/fitting/AppointmentManager';
import InvoiceLangMenu from '../components/InvoiceLangMenu';
import OrderVehicleChip from '../components/OrderVehicleChip';
import VehicleBrandLogo from '../components/VehicleBrandLogo';
import FittingCancelledNotice from '../components/fitting/FittingCancelledNotice';
import { ordersAPI, authAPI } from '../services/api';
import { useT, i18n } from '../services/i18n';
import { carrierLogo } from '../services/carriers';
import { vehicleStore } from '../services/vehicleStore';

const apptDate = (a) => (a?.date && i18n.formatDateTime(a.date)) || a?.date_label || '';

const OrdersPage = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = authAPI.isAuthenticated();

  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const loadOrders = async () => {
    try {
      const [data, carrierList] = await Promise.all([ordersAPI.getAll(), ordersAPI.getCarriers().catch(() => [])]);
      setOrders(data);
      setCarriers(carrierList);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Livré': return 'bg-green-100 text-green-700';
      case 'Expédié': return 'bg-blue-100 text-blue-700';
      case 'En cours': return 'bg-yellow-100 text-yellow-700';
      case 'Annulée': return 'bg-red-100 text-red-700';
      case 'Remboursée': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const STATUS_STEPS = ['En attente', 'En cours', 'Expédié', 'Livré'];
  const PAYMENT_LABELS = { card: 'Carte bancaire', paypal: 'PayPal', apple: 'Apple Pay' };

  const [cancelling, setCancelling] = useState(null);
  const [reordering, setReordering] = useState(null);
  const reorder = async (order) => {
    setReordering(order.id);
    try {
      const r = await ordersAPI.reorder(order.id);
      if (r.vehicle?.brand) vehicleStore.set({ brand: r.vehicle.brand, model: r.vehicle.model, year: r.vehicle.year, engine: r.vehicle.engine_name ? { name: r.vehicle.engine_name } : null, plate: r.vehicle.plate || null });
      if (!r.added.length) { toast.error(t('orders.reorderNone')); return; }
      toast.success(r.skipped.length ? t('orders.reorderPartial', { n: r.added.length, s: r.skipped.length }) : t('orders.reorderOk', { n: r.added.length }));
      navigate('/panier');
    } catch (e) { toast.error(e.response?.data?.detail || t('orders.reorderNone')); } finally { setReordering(null); }
  };
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const vehicleKey = (v) => v?.brand ? `${v.brand}|${v.model || ''}|${v.plate || ''}` : '';
  const vehicleOptions = Object.values(orders.reduce((acc, o) => { const k = vehicleKey(o.vehicle); if (k && !acc[k]) acc[k] = { key: k, vehicle: o.vehicle, count: 0 }; if (k) acc[k].count += 1; return acc; }, {}));
  const noVehicleCount = orders.filter(o => !vehicleKey(o.vehicle)).length;
  const visibleOrders = vehicleFilter === 'all' ? orders : vehicleFilter === 'none' ? orders.filter(o => !vehicleKey(o.vehicle)) : orders.filter(o => vehicleKey(o.vehicle) === vehicleFilter);

  const cancelOrder = async (order) => {
    if (!window.confirm(`Annuler la commande ${order.order_number || ''} ? Les articles seront remis en stock et aucun débit ne sera effectué.`)) return;
    setCancelling(order.id);
    try {
      await ordersAPI.cancel(order.id);
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'Annulée', restocked: true, cancelled_by: 'customer' } : o));
      toast.success(t('orders.cancelledToast'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('orders.cancelError'));
    } finally {
      setCancelling(null);
    }
  };

  const StatusTimeline = ({ status }) => {
    if (status === 'Annulée' || status === 'Remboursée') {
      return (
        <p className="mt-4 text-sm text-gray-600 bg-gray-50 border rounded-md px-3 py-2" data-testid="order-status-closed">
          {status === 'Annulée' ? t('orders.cancelled') : t('orders.refunded')}
        </p>
      );
    }
    const current = STATUS_STEPS.indexOf(status);
    return (
      <div className="flex items-center mt-4" data-testid="order-status-timeline">
        {STATUS_STEPS.map((s, idx) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${idx <= current ? 'bg-orange-500' : 'bg-gray-300'}`} />
              <span className={`text-[10px] mt-1 ${idx <= current ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{t(`status.${s}`)}</span>
            </div>
            {idx < STATUS_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 ${idx < current ? 'bg-orange-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <AccountLayout title={t('acc.orders')} requireAuth={false}>
      <div data-testid="orders-page">
        {isAuthenticated && !isLoading && <><CustomerShippingQuotes /><MpOrdersList /></>}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : !isAuthenticated ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 mb-4">{t('orders.loginPrompt')}</p>
            <Link to="/connexion" className="text-orange-500 hover:underline font-medium">
              {t('orders.login')}
            </Link>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">{t('orders.empty')}</p>
            <Link to="/" className="text-orange-500 hover:underline font-medium">
              {t('cart.discover')}
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {vehicleOptions.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 bg-white rounded-lg shadow-sm px-4 py-3" data-testid="orders-vehicle-filter">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 mr-1">{t('orders.filterVehicle')}</span>
                <button type="button" onClick={() => setVehicleFilter('all')} data-testid="orders-vehicle-filter-all" className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${vehicleFilter === 'all' ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'}`}>{t('orders.filterAll')} ({orders.length})</button>
                {vehicleOptions.map(({ key, vehicle, count }) => (
                  <button key={key} type="button" onClick={() => setVehicleFilter(key)} data-testid={`orders-vehicle-filter-${vehicle.brand}`} className={`inline-flex items-center gap-1.5 text-xs pl-1 pr-3 py-1 rounded-full border transition-colors ${vehicleFilter === key ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-orange-400 hover:text-orange-600'}`}>
                    <VehicleBrandLogo brand={vehicle.brand} model={vehicle.model} size={22} preferModel zoom testId={`orders-vehicle-filter-logo-${vehicle.brand}`} />
                    {vehicle.brand} {vehicle.model}{vehicle.plate ? ` · ${vehicle.plate}` : ''} <span className={vehicleFilter === key ? 'text-orange-100' : 'text-gray-400'}>({count})</span>
                  </button>
                ))}
                {noVehicleCount > 0 && <button type="button" onClick={() => setVehicleFilter('none')} data-testid="orders-vehicle-filter-none" className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${vehicleFilter === 'none' ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'}`}>{t('orders.filterNoVehicle')} ({noVehicleCount})</button>}
              </div>
            )}
            {visibleOrders.length === 0 && <p className="text-sm text-gray-500 text-center py-6" data-testid="orders-filter-empty">{t('orders.filterEmpty')}</p>}
            {visibleOrders.map(order => (
              <div key={order.id} className="bg-white rounded-lg shadow-sm p-6" data-testid="order-card">
                <div className="flex flex-wrap items-start sm:items-center justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900" data-testid="order-number">
                      {t('orders.order', { n: order.order_number || `#${order.id.slice(0, 8).toUpperCase()}` })}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(order.created_at).toLocaleDateString(i18n.locale(), {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                      {order.payment_method && ` · ${PAYMENT_LABELS[order.payment_method] || order.payment_method}`}
                    </p>
                    {(order.carrier_label || order.tracking_carrier) && (
                      <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-full pl-1.5 pr-2.5 py-0.5" data-testid={`order-carrier-${order.id}`}>
                        {carrierLogo(order.tracking_carrier || order.carrier_label) ? <img src={carrierLogo(order.tracking_carrier || order.carrier_label)} alt="" className="h-5 w-12 object-contain no-placeholder" data-testid={`order-carrier-logo-${order.id}`} /> : <Truck size={13} className="text-gray-400" />}
                        {order.tracking_carrier || order.carrier_label}{order.relay_point?.name ? ` · ${order.relay_point.name}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
                    <InvoiceLangMenu order={order} onDownload={(lang) => ordersAPI.downloadInvoice(order, lang)} />
                    {order.payment_status === 'pending' && !['Annulée', 'Remboursée'].includes(order.status) ? (
                      <>
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800" data-testid="order-status">{t('orders.awaitingPayment')}</span>
                        <button type="button" data-testid="order-pay-now" onClick={async () => { const r = await ordersAPI.customerPaymentLink(order.id); navigate(`/payer/${r.token}`); }} className="px-3 py-1.5 rounded-full text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white">{t('orders.payNow')}</button>
                      </>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`} data-testid="order-status">
                        {t(`status.${order.status}`)}
                      </span>
                    )}
                  </div>
                </div>

                <StatusTimeline status={order.status} />
                {order.vehicle?.brand && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <OrderVehicleChip vehicle={order.vehicle} label={t('orders.vehicle')} testId={`order-vehicle-${order.id}`} />
                    <button type="button" data-testid={`order-reorder-${order.id}`} disabled={reordering === order.id} onClick={() => reorder(order)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-full px-3 py-1.5 disabled:opacity-50">
                      {reordering === order.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} {t('orders.reorderVehicle')}
                    </button>
                  </div>
                )}
                {!['Annulée', 'Remboursée'].includes(order.status) && <OrderTrackingTimeline order={order} carriers={carriers} />}

                {order.status === 'En attente' && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-gray-50 border rounded-lg px-4 py-3" data-testid="order-cancel-block">
                    <p className="text-xs text-gray-600">{t('orders.cancelHint')}</p>
                    <button
                      type="button"
                      data-testid={`order-cancel-${order.id}`}
                      disabled={cancelling === order.id}
                      onClick={() => cancelOrder(order)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-md px-3 py-1.5 disabled:opacity-50"
                    >
                      {cancelling === order.id ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} {t('orders.cancel')}
                    </button>
                  </div>
                )}

                {order.refund && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3" data-testid="order-refund-notice">
                    <p className="text-sm text-green-800">{t(order.refund.full ? 'orders.refundCard' : 'orders.refundCardPartial', { a: Number(order.refund.amount).toFixed(2), n: order.refund.credit_note_number })}</p>
                    <button type="button" data-testid={`order-credit-note-${order.id}`} onClick={() => ordersAPI.downloadCreditNote(order, i18n.get())} className="inline-flex items-center gap-1.5 text-sm font-medium text-green-800 border border-green-300 hover:bg-green-100 rounded-md px-3 py-1.5">
                      <FileText size={14} /> {t('orders.creditNotePdf')}
                    </button>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-4 mt-4">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 gap-4">
                      <div>
                        <Link to={`/produit/${item.product_id}`} className="font-medium text-gray-900 hover:text-orange-600">{item.name}</Link>
                        <p className="text-sm text-gray-500">{item.brand}{item.pack_label ? ` · ${item.pack_label} (${item.units} u.)` : ''} - Quantité: {item.quantity}{item.discount_pct ? <span className="ml-2 text-[11px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">−{item.discount_pct}% {item.discount_kind === 'pro' ? 'prix pro' : 'quantité'}</span> : null}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <Link
                          to={`/produit/${item.product_id}?avis=1`}
                          data-testid={`order-review-link-${item.product_id}`}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1 whitespace-nowrap"
                        >
                          <Star size={12} /> {t('orders.review')}
                        </Link>
                        <p className="font-medium text-gray-900">{(item.price * item.quantity).toFixed(2)} €</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 pt-4 mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {order.shipping_info && (
                    <div className="text-sm text-gray-700" data-testid="order-shipping-address">
                      <p className="text-xs font-medium text-gray-500 mb-1">{t('checkout.shippingAddress')}</p>
                      {order.shipping_info.firstName} {order.shipping_info.lastName}<br />
                      {order.shipping_info.address}
                      {order.shipping_info.addressComplement ? `, ${order.shipping_info.addressComplement}` : ''}<br />
                      {order.shipping_info.postalCode} {order.shipping_info.city}<br />
                      {order.shipping_info.phone}
                    </div>
                  )}
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('common.subtotal')}</span>
                      <span>{order.subtotal.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('common.shipping')}</span>
                      <span>{order.shipping === 0 ? t('common.free') : `${order.shipping.toFixed(2)} €`}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg mt-2">
                      <span>{t('common.total')}</span>
                      <span data-testid="order-total">{order.total.toFixed(2)} €</span>
                    </div>
                    {order.fitting && (
                      <div className={`mt-3 text-sm rounded-lg px-3 py-2 ${order.fitting.cancelled ? 'bg-gray-50 text-gray-600 border border-gray-200' : order.fitting.appointment?.status === 'confirmed' || (order.fitting.appointment && !order.fitting.appointment.status) ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`} data-testid="order-fitting">
                        <p><strong>{t('orders.fittingAt', { name: order.fitting.name })}</strong> ({order.fitting.city}) · {t('orders.fitTyres', { n: order.fitting.tyres, count: order.fitting.tyres })} · {Number(order.fitting.total).toFixed(2)} €</p>
                        {order.fitting.cancelled ? <FittingCancelledNotice order={order} /> : order.fitting.appointment ? (order.fitting.appointment.status === 'requested' ? <p className="text-xs mt-0.5" data-testid="order-fitting-requested">{t('orders.fitRequested', { d: apptDate(order.fitting.appointment) })}</p> : <p className="text-xs mt-0.5" data-testid="order-fitting-confirmed">{t('orders.fitConfirmed', { d: apptDate(order.fitting.appointment) })}{order.fitting.appointment.note ? ` — ${order.fitting.appointment.note}` : ''}</p>) : <p className="text-xs mt-0.5">{t('orders.fitContact', { p: order.fitting.phone ? ` (${order.fitting.phone})` : '' })}</p>}
                        <AppointmentManager order={order} onChanged={(fitting, msg) => { setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, fitting } : o))); toast.success(msg); }} />
                        {order.fitting.review_token && !order.fitting.review_id && <a href={`/montage/avis/${order.fitting.review_token}`} className="inline-block mt-1 text-xs font-semibold underline" data-testid="order-fitting-review-link">{t('orders.fitReview')}</a>}
                        {order.fitting.review_rating && <p className="text-xs mt-0.5">{t('orders.fitRating', { r: order.fitting.review_rating })}</p>}
                      </div>
                    )}
                    <BillingAddress order={order} className="mt-3 p-3 bg-gray-50 rounded-lg" />
                    {order.wallet_refund && <p className="mt-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-1.5" data-testid="order-wallet-refund">Remboursée en avoir : {Number(order.wallet_refund.amount).toFixed(2)} € crédités sur votre compte de dépôt</p>}
                    {order.wallet_used > 0 && (
                      <div className="flex justify-between text-sm text-green-700" data-testid="order-wallet-used">
                        <span>{t('orders.walletUsed')}</span>
                        <span>−{order.wallet_used.toFixed(2)} € · {t('orders.paid', { amount: Number(order.paid_amount ?? order.total - order.wallet_used).toFixed(2) })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AccountLayout>
  );
};

export default OrdersPage;
